import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useChatDock } from "../../stores/chat";
import { useSession } from "../../stores/session";
import { messages, type Conversation } from "./api";
import { ChatLauncher } from "./ChatLauncher";
import { ChatPanel, ChatTab } from "./DockedConversation";

/**
 * Conversations, docked at the right-hand end of the player bar (§9.2).
 *
 * ## Why it lives in the bar rather than on a page
 *
 * For the same reason the audio does. A conversation you are having while
 * browsing should not end because you opened a track, and a page that owns it
 * ends it on every navigation. The bar is mounted in the shell, outside the
 * router, so a window opened here outlives everything that could have opened
 * it.
 *
 * ## Desktop only, and that is in the spec rather than a shortcut
 *
 * §9.2: below 768px chat drops out of the bar entirely and becomes the
 * `/messages` route. A 320px window docked inside a 360px viewport is a
 * full-screen sheet wearing a costume, and the route is already the better
 * version of that.
 */
export function DockChat() {
  const user = useSession((state) => state.user);
  const tabs = useChatDock((state) => state.tabs);
  const open = useChatDock((state) => state.open);
  const people = useChatDock((state) => state.people);
  const fit = useChatDock((state) => state.fit);

  /*
   * Collapse whatever stops fitting when the window narrows.
   *
   * Without this the panels keep their count through a resize and start
   * overlapping again at the small end - which is the exact bug tiling was
   * meant to remove, reintroduced by dragging the browser edge.
   */
  useEffect(() => {
    const onResize = () => fit();

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [fit]);

  /*
   * The inbox, which the header's envelope is already polling.
   *
   * Shared query key, so docking a conversation costs no request of its own and
   * the unread badge on a tab agrees with the one on the envelope - two
   * counters for the same thing disagreeing is how people stop trusting either.
   */
  const inbox = useQuery({
    queryKey: ["conversations"],
    queryFn: messages.inbox,
    enabled: Boolean(user),
    staleTime: 15_000,
  });

  /*
   * Signed in is the only condition.
   *
   * This used to also require a docked tab, which made the rail invisible until
   * something else had docked one - and the only things that could were a
   * profile's Message button and the launcher that was not being rendered. A
   * fresh load showed a bar with no chat in it and no way to get any.
   */
  if (!user) return null;

  const byId = new Map<number, Conversation>(
    (inbox.data?.items ?? []).map((item) => [item.id, item]),
  );

  /*
   * The inbox where it has the conversation, the remembered correspondent
   * where it does not - which is every conversation created in the last few
   * seconds, since one with no messages is not in the list yet.
   */
  const identify = (id: number): Conversation | null =>
    byId.get(id)
    ?? (people[id]
      ? {
          id,
          type: 'dm',
          title: null,
          last_message_at: null,
          unread: 0,
          muted: false,
          preview: '',
          other: people[id],
        }
      : null);

  return (
    <div
      className="relative hidden h-full md:flex md:items-stretch"
      data-dock-chat
    >
      {/*
        The open windows, tiled from the right edge of the bar rather than each
        hanging off its own tab - see `DockedConversation`. `pointer-events-none`
        on the strip so the empty run to the left of them is still the page.
      */}
      <div className="pointer-events-none absolute bottom-full right-0 flex items-end gap-2">
        {open.map((id) => (
          <ChatPanel key={id} conversationId={id} conversation={identify(id)} />
        ))}
      </div>
      {/*
        Right to left, newest nearest the edge.

        `flex-row-reverse` over reversing the array: the store keeps tabs in the
        order they were opened, and a list that reads backwards in the DOM is
        one that reads backwards to a screen reader too.
      */}
      {tabs.map((id) => (
        <ChatTab key={id} conversationId={id} conversation={identify(id)} />
      ))}

      {/* The anchor, at the right-hand end, so the tabs grow away from it. */}
      <ChatLauncher conversations={inbox.data?.items ?? []} loading={inbox.isPending} />
    </div>
  );
}
